import { useMemo, useState } from 'react'
import { Loader2, Plus } from 'lucide-react'
import { toast } from '@atrvd/saas-ui'
import { Tabs, TabsContent, TabsList, TabsTrigger, PageHeader, DatePicker, MultiSelect, Button, Modal } from '@atrvd/saas-ui'
import { KpiCards, ManualTransactionsTable, ManualTransactionFormDialog, ManualTransactionBatchFormDialog } from '@/components/banking'
import { FileImportSection } from '@/components/import/FileImportSection'
import { transactionImportConfig } from '@/components/import/import-configs'
import {
  useBankingKpi,
  useFacilities,
  useManualTransactions,
  useManualTransactionCount,
  useDeleteManualTransaction,
} from '@/hooks/useBanking'
import { useTablePagination } from '@/hooks/useTablePagination'
import { pageOrCountError, tableQueryState } from '@/components/common/queryErrorDisplay'
import { toLocalDateString, safeParseDate } from '@/lib/date-utils'
import type { TransactionsImportTab, ManualTransactionFilters } from '@/types/banking'
import type { TenantTransaction } from '@/lib/db-tenant'

const EMPTY_MANUAL_TRANSACTIONS: TenantTransaction[] = []

/**
 * Transactions Import page
 * Displays KPI cards, date/facility filters, and tabbed content
 * including the manual transactions list with edit/delete actions.
 */
export function TransactionsImportPage() {
  const [activeTab, setActiveTab] = useState<TransactionsImportTab>('transactions-manual-input')

  // Filters: Date range + Facility (per AC)
  const [startDate, setStartDate] = useState<string | undefined>()
  const [endDate, setEndDate] = useState<string | undefined>()
  const [facilityIds, setFacilityIds] = useState<string[]>([])

  const manualFiltersBase: ManualTransactionFilters = useMemo(() => ({
    startDate,
    endDate,
    facilityIds: facilityIds.length ? facilityIds : undefined,
  }), [startDate, endDate, facilityIds])

  const {
    data: manualTotalCount,
    isFetching: manualCountFetching,
    error: manualCountError,
    refetch: refetchManualCount,
  } = useManualTransactionCount(manualFiltersBase)
  // Any filter change — including clearing a date — or a tab switch starts over on page 1.
  const pagination = useTablePagination({
    totalItems: manualTotalCount,
    resetKey: JSON.stringify([startDate, endDate, facilityIds, activeTab]),
  })

  const manualFiltersWithPagination = useMemo(() => ({
    ...manualFiltersBase,
    limit: pagination.pageSize,
    offset: pagination.offset,
  }), [manualFiltersBase, pagination.pageSize, pagination.offset])

  // KPI filters — same date/facility scope
  const kpiFilters = useMemo(() => ({
    startDate,
    endDate,
    facilityIds: facilityIds.length ? facilityIds : undefined,
  }), [startDate, endDate, facilityIds])

  const [isEditFormOpen, setIsEditFormOpen] = useState(false)
  const [editingTransaction, setEditingTransaction] = useState<TenantTransaction | undefined>()
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false)
  /** State for delete confirmation dialog */
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [txToDelete, setTxToDelete] = useState<TenantTransaction | null>(null)

  const deleteTransaction = useDeleteManualTransaction()

  // Fetch data
  const { data: kpi, isLoading: kpiLoading } = useBankingKpi(kpiFilters)
  const { data: facilitiesData } = useFacilities()
  const facilities = facilitiesData ?? []
  const manualQuery = useManualTransactions(manualFiltersWithPagination)
  const manualTransactions = manualQuery.data ?? EMPTY_MANUAL_TRANSACTIONS
  const manualTableState = tableQueryState(
    {
      isPending: manualQuery.isPending,
      isPlaceholderData: manualQuery.isPlaceholderData,
      isFetching: manualQuery.isFetching || manualCountFetching,
      error: pageOrCountError(manualQuery.error, manualCountError),
      refetch: () => {
        void manualQuery.refetch()
        void refetchManualCount()
      },
    },
    manualTransactions.length,
  )

  /** Clear all filters (the page goes back to 1 through the pagination resetKey). */
  const clearManualFilters = () => {
    setStartDate(undefined)
    setEndDate(undefined)
    setFacilityIds([])
  }

  /** Open edit dialog with the selected transaction */
  const handleEdit = (tx: TenantTransaction) => {
    setEditingTransaction(tx)
    setIsEditFormOpen(true)
  }

  /** Open the delete confirmation dialog */
  const handleDelete = (tx: TenantTransaction) => {
    setTxToDelete(tx)
    setDeleteConfirmOpen(true)
  }

  /** Confirm and execute transaction deletion */
  const confirmDelete = async () => {
    if (!txToDelete) return

    try {
      await deleteTransaction.mutateAsync(txToDelete.id)
      toast.success('Transaction deleted')
      setDeleteConfirmOpen(false)
      setTxToDelete(null)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete transaction'
      toast.error(message)
    }
  }

  /** Close edit dialog and clear the editing transaction state */
  const handleEditFormClose = () => {
    setIsEditFormOpen(false)
    setEditingTransaction(undefined)
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Transactions Import"
        description="Import and manage banking transactions"
      />

      {/* Filters: Date range + Facility */}
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-muted-foreground">From</label>
          <DatePicker
            date={safeParseDate(startDate)}
            onDateChange={(date) => {
              if (!date) { setStartDate(undefined); return }
              const dateStr = toLocalDateString(date)
              if (endDate && dateStr > endDate) setEndDate(dateStr)
              setStartDate(dateStr)
            }}
            placeholder="Start date"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-muted-foreground">To</label>
          <DatePicker
            date={safeParseDate(endDate)}
            onDateChange={(date) => {
              if (!date) { setEndDate(undefined); return }
              const dateStr = toLocalDateString(date)
              if (startDate && dateStr < startDate) setStartDate(dateStr)
              setEndDate(dateStr)
            }}
            placeholder="End date"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-muted-foreground">Facility</label>
          <MultiSelect
            options={facilities.map((f) => ({ value: f.id, label: f.name }))}
            value={facilityIds}
            onChange={setFacilityIds}
            placeholder="All Facilities"
            className="w-48"
            searchable
            searchPlaceholder="Search facilities..."
          />
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Add Transaction */}
        <Button
          className="bg-red-600 hover:bg-red-700 text-white"
          onClick={() => setIsCreateFormOpen(true)}
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Transaction
        </Button>
      </div>

      {/* KPI Cards */}
      <KpiCards kpi={kpi} loading={kpiLoading} />

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as TransactionsImportTab)}
      >
        {/* DCP-269: only the two implemented tabs are shown. The empty
            "coming soon" placeholder tabs (Account Import Status, Transactions
            categorization, API import, Import History) are hidden until built. */}
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="transactions-files-upload">
            Transactions files upload
          </TabsTrigger>
          <TabsTrigger value="transactions-manual-input">
            Transactions manual input
          </TabsTrigger>
        </TabsList>

        <TabsContent value="transactions-files-upload" className="mt-6">
          <FileImportSection config={transactionImportConfig} />
        </TabsContent>

        <TabsContent value="transactions-manual-input" className="mt-6 space-y-4">
          <ManualTransactionsTable
            transactions={manualTransactions}
            {...manualTableState}
            onClearFilters={clearManualFilters}
            onEdit={handleEdit}
            onDelete={handleDelete}
            pagination={pagination.dataTablePagination}
          />
        </TabsContent>
      </Tabs>
      {/* Edit Transaction Dialog */}
      <ManualTransactionFormDialog
        open={isEditFormOpen}
        onOpenChange={handleEditFormClose}
        transaction={editingTransaction}
      />

      {/* Create Transactions Dialog (batch) */}
      <ManualTransactionBatchFormDialog
        open={isCreateFormOpen}
        onOpenChange={(open) => { if (!open) setIsCreateFormOpen(false) }}
      />

      {/* Delete transaction confirmation dialog */}
      <Modal
        isOpen={deleteConfirmOpen}
        onClose={() => { setDeleteConfirmOpen(false); setTxToDelete(null) }}
        title="Delete Transaction"
        description="This action cannot be undone"
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => { setDeleteConfirmOpen(false); setTxToDelete(null) }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleteTransaction.isPending}
            >
              {deleteTransaction.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Delete
            </Button>
          </div>
        }
      >
        <p>Are you sure you want to delete this transaction?</p>
      </Modal>
    </div>
  )
}
